// QuestionShell (board 25 v2 §أ «سؤال واحد في كل شاشة»): the brand panel on
// the start side, then the main column — offline strip, the thin progress
// line (counts screens), the answer chips (previous answers; each reopens its
// question) with the «n/N · label» counter (counts steps), ONE question (40 px;
// 34 at 1024; 28 below 768), one large answer widget, one coral button with
// «Enter ↵», the next question faded below, and the footer links. Below 768
// there is no panel: mark 32 + counter on one line, the chips as a row, the
// button pinned at the bottom. System errors replace the question with the
// error card (§5.10) in the same column — never a modal.

import { useEffect, useMemo, useRef, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, CloudOff, HelpCircle } from 'lucide-react';
import { BrandMark, type BrandMarkMode } from '@/components/BrandMark';
import { PrimaryAction } from '@/components/Button';
import { useOnline } from '@/components/OfflineStrip';
import { sessionStore } from '@/lib/auth';
import { flagOn, useConfigQuery } from '@/lib/config';
import { formatIraqiPhone, toArabicDigits } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { BRANCHES_Q, ERRORS, HOURS, PIN, ROLE, SHELL, STEP_LABELS } from '../copy';
import { footerLinksFromConfig, type FooterLinks } from '../data/api';
import { ONB } from '../data/flow';
import { SHAPES, shapeOf } from '../data/shapes';
import { profileOf } from '../preview';
import { onboardingStore, useOnboarding } from '../state';
import { counterText, progressOf, screenIndex, screensOf, stepOf, type PathOptions, type ScreenKey } from '../steps';
import { BrandPanel, type PanelProps } from './BrandPanel';
import { maskedPhone } from './widgets';

export interface WizardError {
  kind: 'network' | 'server';
  ref?: string | null;
  /** A 4xx's own sentence: replaces the generic server body. */
  detail?: string | null;
  onRetry: () => void;
}

export interface WizardProps {
  screen: ScreenKey;
  testId: string;
  /** data-state for tests and the design pass. */
  state?: string;
  question?: ReactNode;
  sub?: ReactNode;
  /** List screens (hours, team): the question one size smaller, tighter gaps. */
  list?: boolean;
  children?: ReactNode;
  /** The screen's ONE coral action (omit on screens with no decision — join--waiting). */
  primary?: ReactNode;
  /** A neutral second button (e.g. «رقم آخر», «استيراد من المربع»). */
  secondary?: ReactNode;
  /** The teal text link at the end of the actions row. */
  tertiary?: ReactNode;
  /** «أو اضغط Enter ↵» after the coral (desktop only). */
  enterHint?: boolean;
  /** The next question, faded below (default state only). */
  peek?: string;
  onSubmit?: () => void;
  error?: WizardError | null;
  panel: PanelProps;
  /** The mark above the column on a phone (default static; launch on role; failed on error cards). */
  mobileMark?: BrandMarkMode;
  /** Centre the column's content (done on a phone). */
  centered?: boolean;
  /** Override the computed path (tests / the join screens). */
  path?: Partial<PathOptions>;
}

export interface Crumb {
  label: string;
  ltr?: boolean;
  to?: string;
}

/** The previous answers as chips — everything answered BEFORE `screen`, in path order. */
export function crumbsFor(screen: ScreenKey, opts: PathOptions): Crumb[] {
  const s = onboardingStore.get();
  const authed = sessionStore.isAuthenticated();
  const phone = authed ? (sessionStore.getUser()?.phone ?? '') : onboardingStore.enrollment().phone;
  const screens = screensOf(opts);
  const at = screenIndex(screen, opts, screens);
  const before = (k: ScreenKey) => {
    const i = screenIndex(k, opts, screens);
    return i >= 0 && at >= 0 && i < at;
  };
  const out: Crumb[] = [];
  // The celebration screens carry no chips (board: crumbList [] on done / accepted).
  if (screen === 'done' || screen === 'accepted') return out;
  if (opts.intent === 'member') {
    if (before('role')) out.push({ label: screen === 'waiting' ? ROLE.crumbStaff : ROLE.crumbMember, to: authed ? undefined : ONB.role });
    if (before('phone') && phone) out.push({ label: screen === 'otp' ? maskedPhone(phone) : formatIraqiPhone(phone), ltr: true, to: authed ? undefined : ONB.phone });
    if (before('pin') && authed) out.push({ label: PIN.crumb });
    return out;
  }
  const locked = !!s.createdId;
  if (before('role')) out.push({ label: ROLE.crumbOwner, to: authed ? undefined : ONB.role });
  if (before('phone') && phone) out.push({ label: screen === 'otp' ? maskedPhone(phone) : formatIraqiPhone(phone), ltr: true, to: authed ? undefined : ONB.phone });
  if (before('pin') && authed) out.push({ label: PIN.crumb });
  if (before('name') && s.draft.name.trim()) out.push({ label: s.draft.name.trim(), to: locked ? undefined : ONB.name });
  if (before('type') && s.type) out.push({ label: s.type.name_ar, to: locked ? undefined : ONB.type });
  if (before('city') && s.draft.city.trim()) out.push({ label: s.draft.city.trim(), to: locked ? undefined : ONB.city });
  if (before('branches') && s.branches.multi && s.branches.firstName.trim()) out.push({ label: s.branches.firstName.trim(), to: locked ? undefined : ONB.branches });
  if (before('address') && s.draft.area.trim()) out.push({ label: s.draft.area.trim(), to: locked ? undefined : ONB.address });
  if (before('bphone') && s.draft.phone.trim()) out.push({ label: formatIraqiPhone(s.draft.phone), ltr: true, to: locked ? undefined : ONB.bphone });
  // The step-5 chip follows the shape: «ساعات الدوام» / «أيام العمل» / «الاستلام والتسليم» (seat: none).
  const step5 = SHAPES[opts.shape ?? 'staff'].step5;
  if (step5 !== 'none' && before(step5)) out.push({ label: step5 === 'hours' ? HOURS.crumb : STEP_LABELS[step5], to: locked ? undefined : ONB[step5] });
  return out;
}

/**
 * «عندك أكثر من فرع؟» is asked only when the platform's global
 * `business.branches` is on in /v1/config (ruling R12: before the business
 * exists there is no tenant layer to ask). Absent = skipped.
 */
export function useBranchesQuestion(): { on: boolean; known: boolean } {
  const config = useConfigQuery();
  return { on: flagOn(config.data?.flags, 'business.branches'), known: !!config.data || config.isError };
}

/** The path the current state implies (role + category shape + the branches flag + invite code). */
export function usePathOptions(over?: Partial<PathOptions>): PathOptions {
  const s = useOnboarding();
  const branches = useBranchesQuestion().on;
  return useMemo(
    // N is fixed the moment the role is chosen (owner 6, the staff map); it drops to 5 only once a seat category is picked.
    () => ({ intent: s.intent, shape: shapeOf(s.type), branchesQuestion: branches, withCode: !!s.invite, ...over }),
    [s.intent, s.type, s.invite, branches, over],
  );
}

/** The question after the city: the branches question when it applies, else the address. */
export function useAfterCity(): { to: string; peek: string; known: boolean } {
  const { on, known } = useBranchesQuestion();
  return on ? { to: ONB.branches, peek: BRANCHES_Q.title, known } : { to: ONB.address, peek: '', known };
}

/** The next question peeks only while the current one is calm (no error / busy state on screen). */
const PEEK_STATES = new Set(['default', 'code', 'set', 'confirm', 'loading']);

const MAX_CRUMBS = 4;

function Crumbs({ list }: { list: Crumb[] }) {
  const shown = list.length > MAX_CRUMBS ? list.slice(-MAX_CRUMBS) : list;
  const more = list.length - shown.length;
  return (
    <div className="onb-crumbs" data-testid="onb-crumbs">
      {more > 0 && <span className="onb-crumb onb-crumb--more">{SHELL.more(toArabicDigits(more))}</span>}
      {shown.map((c, i) => {
        const inner = (
          <>
            <Check aria-hidden="true" />
            {c.ltr ? (
              <bdi dir="ltr" className="ltr">
                {c.label}
              </bdi>
            ) : (
              c.label
            )}
          </>
        );
        return c.to ? (
          <Link key={i} to={c.to} className="onb-crumb" data-testid="onb-crumb">
            {inner}
          </Link>
        ) : (
          <span key={i} className="onb-crumb" data-testid="onb-crumb">
            {inner}
          </span>
        );
      })}
    </div>
  );
}

export function FooterLinksRow({ links }: { links: FooterLinks }) {
  const items: Array<[string, string | undefined]> = [
    [SHELL.terms, links.terms],
    [SHELL.privacy, links.privacy],
    [SHELL.help, links.help],
  ];
  const shown = items.filter((x) => !!x[1]);
  if (shown.length === 0) return null;
  return (
    <footer className="onb-links">
      {shown.map(([label, href]) => (
        <a key={label} href={href} target="_blank" rel="noreferrer">
          {label}
        </a>
      ))}
    </footer>
  );
}

export function useFooterLinks(): FooterLinks {
  const config = useConfigQuery();
  return useMemo(() => footerLinksFromConfig(config.data), [config.data]);
}

/** The error card in place of the question (§5.10; §7 server line with the request id). */
export function ErrorBlock({ kind, refId, detail }: { kind: 'network' | 'server'; refId?: string | null; detail?: string | null }) {
  return (
    <div className="onb-errblock" role="alert" data-kind={kind} data-testid="onb-error">
      <div className="onb-errblock-title">{ERRORS.title}</div>
      <div className="onb-errblock-body">{kind === 'server' ? detail || ERRORS.server : ERRORS.network}</div>
      {kind === 'server' && refId && (
        <div className="onb-errblock-ref">
          {ERRORS.ref}{' '}
          <bdi dir="ltr" className="ltr">
            {refId}
          </bdi>
        </div>
      )}
    </div>
  );
}

export function Wizard({
  screen,
  testId,
  state = 'default',
  question,
  sub,
  list,
  children,
  primary,
  secondary,
  tertiary,
  enterHint,
  peek,
  onSubmit,
  error,
  panel,
  mobileMark = 'static',
  centered,
  path,
}: WizardProps) {
  const layout = useLayout();
  const online = useOnline();
  const opts = usePathOptions(path);
  const links = useFooterLinks();
  const mobile = layout === 'phone';
  const s = useOnboarding();
  const step = stepOf(screen, opts, SHAPES[opts.shape ?? 'staff'].stepLabel(profileOf(s.type?.id), s.type?.id ?? ''));
  const progress = progressOf(screen, opts);
  const crumbs = useMemo(() => crumbsFor(screen, opts), [screen, opts, s]);
  const formRef = useRef<HTMLFormElement>(null);

  // A reload mid-flow: the enrollment is gone — the screens guard themselves.
  useEffect(() => {
    formRef.current?.querySelector<HTMLElement>('[autofocus]')?.focus();
  }, [screen]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (error) {
      error.onRetry();
      return;
    }
    onSubmit?.();
  };
  // Enter continues on screens whose answer is a chip or a card (no text input focused).
  const onKey = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter' || !onSubmit || error) return;
    const t = e.target as HTMLElement;
    const tag = t.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return;
    e.preventDefault();
    onSubmit();
  };

  const counter = counterText(step);
  const showPanel = !mobile && layout !== 'tablet';
  const failed = !!error;

  return (
    <div className="onb" data-testid={testId} data-state={failed ? `error-${error.kind}` : state} data-screen={screen} data-layout={layout} dir="rtl">
      {showPanel && <BrandPanel {...panel} mark={failed ? 'failed' : panel.mark} compact={layout === 'compact'} />}
      <form ref={formRef} className={`onb-main${centered ? ' onb-main--centered' : ''}`} onSubmit={submit} onKeyDown={onKey} noValidate>
        {!online && (
          <div className="onb-offline" role="status" data-testid="onb-offline">
            <CloudOff aria-hidden="true" />
            {SHELL.offline}
          </div>
        )}
        <div className="onb-progress" aria-hidden="true">
          <div className="onb-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <div className="onb-head">
          {mobile ? (
            <>
              <BrandMark mode={failed ? 'failed' : mobileMark} size={32} label="احجزلي" className="onb-head-mark" />
              {counter && (
                <span className="onb-counter" data-testid="onb-step">
                  {counter}
                </span>
              )}
            </>
          ) : (
            <>
              {crumbs.length > 0 ? <Crumbs list={crumbs} /> : <span />}
              {counter && (
                <span className="onb-counter" data-testid="onb-step">
                  {counter}
                </span>
              )}
            </>
          )}
        </div>
        {mobile && crumbs.length > 0 && (
          <div className="onb-head-crumbs">
            <Crumbs list={crumbs} />
          </div>
        )}
        <div className="onb-body">
          <div className={`onb-col${list ? ' onb-col--list' : ''}`} key={failed ? `${screen}-error` : screen}>
            {failed ? (
              <>
                <ErrorBlock kind={error.kind} refId={error.ref} detail={error.detail} />
                <fieldset className="onb-actions" disabled={!online}>
                  <PrimaryAction type="submit" size="lg" block={mobile} data-testid="onb-retry">
                    {SHELL.retry}
                  </PrimaryAction>
                  {error.kind === 'server' && links.help && (
                    <a className="onb-tert" href={links.help} target="_blank" rel="noreferrer">
                      <HelpCircle aria-hidden="true" />
                      {SHELL.help}
                    </a>
                  )}
                </fieldset>
              </>
            ) : (
              <>
                {question && (
                  <div className="onb-q">
                    <h1 className="onb-title">{question}</h1>
                    {sub && <p className="onb-sub">{sub}</p>}
                  </div>
                )}
                {children}
                {(primary || secondary || tertiary) && (
                  <fieldset className="onb-actions" disabled={!online} data-testid="onb-actions">
                    {primary}
                    {enterHint && !mobile && online && (
                      <span className="onb-enter" aria-hidden="true">
                        {SHELL.enter}{' '}
                        <kbd dir="ltr" className="ltr">
                          {SHELL.enterKey}
                        </kbd>
                      </span>
                    )}
                    {secondary}
                    {tertiary && <span className="onb-tert-slot">{tertiary}</span>}
                  </fieldset>
                )}
              </>
            )}
          </div>
          {peek && !failed && PEEK_STATES.has(state) && !mobile && (
            <div className="onb-peek" aria-hidden="true" data-testid="onb-peek">
              {peek}
            </div>
          )}
        </div>
        {!mobile && <FooterLinksRow links={links} />}
      </form>
      <span hidden data-testid="onb-progress" data-value={progress} />
    </div>
  );
}
